import type { AxiosProgressEvent } from 'axios';

import { Pipelines, TrainingConfigs } from 'dive-common/apispec';
import girderRest from 'platform/web-girder/plugins/girder';

export interface BrandData {
  vuetify?: unknown;
  favicon?: string;
  logo?: string;
  name?: string;
  loginMessage?: string;
  alertMessage?: string;
  trainingMessage?: string;
}

/** The served Fast-FoundationStereo export, as `dive_configuration/stereo_foundation_model/spec` reports it. */
export interface StereoFoundationModelSpec {
  name: string;
  url: string;
  md5: string;
  /** Input size from the ONNX list or a sidecar yaml; null when unknown (read from the graph instead). */
  height: number | null;
  width: number | null;
  size: number;
}

export interface StatsResponse {
  table_stats: {
    datasets: number; // Total count of datasets
    jobs: {
      [jobType: string]: number; // Count of jobs by type
    };
    newUsers: number; // Count of new users within the selected date range
    totalUsers: number; // Total count of all users (not date-range filtered)
  };
  groupByUser?: {
    datasets: {
      [username: string]: number; // Datasets per user
    };
    jobs: {
      [username: string]: number; // Jobs per user
    };
  };
  groupByMonth?: {
    datasets: {
      [yearMonth: string]: number; // Datasets by year-month
    };
    newUsers: {
      [yearMonth: string]: number; // New users by year-month
    };
    jobs: {
      [yearMonth: string]: number; // Total jobs by year-month
    };
  };
}

export type DateRange = '60 days' | '3 months' | '6 months' | '1 year' | '3 years' | '5 years' | undefined;

export type GroupBy = 'user' | 'month' | undefined;

export interface AnnotationStatsLabelCount {
  label: string;
  count: number;
}

export interface AnnotationStatsMedia {
  /** One per video dataset / multicam camera — original + transcoded pair counts once. */
  videos: number;
  imageSequences: number;
  /** Individual image files across image-sequence and large-image datasets. */
  images: number;
}

export interface AnnotationStatsCurationBreakdown {
  datasets: number;
  tracks: number;
  detections: number;
  confidenceGte1: number;
  manualTracks: number;
  correctedTracks: number;
  /** Detection counts by geometry type; Polygon/Point/LineString overlap, boundingBoxOnly is exclusive. */
  geometryTypes: {
    Polygon: number;
    Point: number;
    LineString: number;
    boundingBoxOnly: number;
    [type: string]: number;
  };
  detectionsWithPolygon: number;
  detectionsWithPoint: number;
  detectionsWithLineString: number;
  detectionsWithGeometry: number;
  detectionsBoundsOnly: number;
  /** Bounds anchored at (0, 0) with positive extent — full-frame proxy. */
  fullFrameDetections: number;
  tracksWithPolygon: number;
  tracksWithPoint: number;
  tracksWithLineString: number;
  tracksWithGeometry: number;
  tracksBoundsOnly: number;
  tracksFullFrame: number;
  media: AnnotationStatsMedia;
}

export interface AnnotationStatsReport {
  generatedAt: string;
  datasets: {
    total: number;
    mostlyManual: number;
    mixedSubstantialCorrections: number;
    mostlyComputed: number;
    empty: number;
  };
  tracks: {
    total: number;
    confidenceGte1: number;
    /** Sum of stored features over live tracks; absent in reports saved before this field existed. */
    detections?: number;
  };
  /** Logical media totals; absent in reports saved before this field existed. */
  media?: AnnotationStatsMedia;
  /** Per-curation-class breakdown; absent in reports saved before this field existed. */
  curationBreakdown?: Record<
    'mostlyManual' | 'mixedSubstantialCorrections' | 'mostlyComputed' | 'empty',
    AnnotationStatsCurationBreakdown
  >;
  labels: AnnotationStatsLabelCount[];
  /**
   * Distinct users who uploaded a dataset or modified/saved tracks in each
   * window ending at `generatedAt`; absent in reports saved before this existed.
   */
  activeUsers?: {
    lastMonth: number;
    last3Months: number;
    last6Months: number;
    lastYear: number;
    last5Years: number;
  };
}

export interface AnnotationStatsJobResponse {
  jobId: string;
}

export type AddOns = [string, string, string, boolean][];

export interface DiveConfiguration {
  distributedWorker?: string;
  pipelinesEnabled?: boolean;
  trainingEnabled?: boolean;
  jobsDisabled?: boolean;
  jobsDisabledMessage?: string;
}

export interface JobsDisabledConfig {
  disabled: boolean;
  message: string;
}

export const DEFAULT_JOBS_DISABLED_MESSAGE = 'Updates will be happening soon, we are disabling jobs until after the updates';

function getConfig() {
  return girderRest.get<DiveConfiguration>('dive_configuration');
}

function getBrandData() {
  return girderRest.get<BrandData>('dive_configuration/brand_data');
}

function putBrandData(brandData: BrandData) {
  return girderRest.put('dive_configuration/brand_data', brandData);
}

function putJobsDisabled(config: JobsDisabledConfig) {
  return girderRest.put<JobsDisabledConfig>('dive_configuration/jobs_disabled', config);
}

function getPipelineList() {
  return girderRest.get<Pipelines>('dive_configuration/pipelines');
}

function getTrainingConfigurations() {
  return girderRest.get<TrainingConfigs>('dive_configuration/training_configs');
}

function getAddons() {
  return girderRest.get<AddOns>('dive_configuration/addons');
}

export interface ImagerySize {
  width: number;
  height: number;
}

/** `imagery` lets the server pick the export whose input fits the frames. */
function getStereoFoundationModelSpec(imagery?: ImagerySize) {
  return girderRest.get<StereoFoundationModelSpec>('dive_configuration/stereo_foundation_model/spec', {
    params: imagery,
  });
}

/**
 * `onProgress` reports downloaded bytes so the caller can show a determinate
 * bar for the ~100 MB export. `total` is absent when the length is not
 * computable, e.g. a proxy re-encoded the stream.
 */
function getStereoFoundationModel(
  imagery?: ImagerySize,
  onProgress?: (loaded: number, total?: number) => void,
) {
  return girderRest.get<ArrayBuffer>('dive_configuration/stereo_foundation_model', {
    responseType: 'arraybuffer',
    params: imagery,
    onDownloadProgress: onProgress
      ? (event: AxiosProgressEvent) => onProgress(event.loaded, event.total)
      : undefined,
  });
}

function postAddons(urls: string[], forceDownload: boolean) {
  return girderRest.post(`dive_configuration/upgrade_pipelines?force=${forceDownload}`, urls);
}

function updateContainers() {
  return girderRest.post('dive_configuration/update_containers');
}

function getStats(dateRange?: DateRange, overrideDateTime?: string, groupBy?: GroupBy, limit?: number) {
  return girderRest.get<StatsResponse>('dive_configuration/stats', {
    params: {
      dateRange,
      overrideDateTime,
      groupBy,
      limit,
    },
  });
}

function startAnnotationStatsJob() {
  return girderRest.post<AnnotationStatsJobResponse>('dive_configuration/annotation_stats');
}

/** Latest report saved under the admin user's Stats folder, or null. */
function getLatestAnnotationStats() {
  return girderRest.get<AnnotationStatsReport | null>('dive_configuration/annotation_stats');
}

export {
  getBrandData,
  getConfig,
  putBrandData,
  putJobsDisabled,
  getPipelineList,
  getTrainingConfigurations,
  getAddons,
  getStereoFoundationModelSpec,
  getStereoFoundationModel,
  postAddons,
  updateContainers,
  getStats,
  startAnnotationStatsJob,
  getLatestAnnotationStats,
};
